import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { t } from 'i18next';
import moment from 'moment';
import React, { useState, useRef } from 'react';
import { FaEdit } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import { useToast } from '../../core/contexts/toast';
import { ModeloSopProduction } from '../../core/models/sopProduction';
import { RepositorioSopProduction } from '../../core/repositories/sopProductionRepositorio';
import { formatarPorcentagem, formatLargeNumber, isNumeric } from '../../core/utils/funcoes';

interface Props {
  lista: ModeloSopProduction[];
  setLista: React.Dispatch<React.SetStateAction<ModeloSopProduction[]>>;
}

const SopProductionTable: React.FC<Props> = (props) => {
  const gridApi = useRef<any>(null);
  const { mostrarToast } = useToast();
  const [loading, setLoading] = useState(false);

  const columnDefs: ColDef<ModeloSopProduction>[] = [
    { headerName: 'Business Unit', field: 'des_business_unit', minWidth: 120, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Month/Year', field: 'des_time', minWidth: 100, cellRenderer: (params: any) => <CustomSkeleton value={params.value} date attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Product', field: 'des_product', minWidth: 120, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Product Family', field: 'des_product_family', minWidth: 150, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Plant', field: 'des_industrial_plant', minWidth: 120, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'Packaging Real (units)', field: 'num_packaging_real_units', minWidth: 140, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Packaging Forecast (units)', field: 'num_packaging_forecast_units', minWidth: 160, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Packaging Budget (units)', field: 'num_packaging_budget_units', minWidth: 160, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'Flavor Real (units)', field: 'num_flavor_real_units', minWidth: 130, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Flavor Forecast (units)', field: 'num_flavor_forecast_units', minWidth: 150, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Flavor Budget (units)', field: 'num_flavor_budget_units', minWidth: 150, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'Drying Real (units)', field: 'num_drying_real_units', minWidth: 130, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Drying Forecast (units)', field: 'num_drying_forecast_units', minWidth: 150, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Drying Budget (units)', field: 'num_drying_budget_units', minWidth: 150, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'Blending Real (units)', field: 'num_blending_real_units', minWidth: 130, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Blending Forecast (units)', field: 'num_blending_forecast_units', minWidth: 150, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Blending Budget (units)', field: 'num_blending_budget_units', minWidth: 150, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'Total Production Real (USD)', field: 'num_total_production_real_usd', minWidth: 160, cellRenderer: (params: any) => <CustomSkeleton value={params.value} money attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Total Production Forecast (USD)', field: 'num_total_production_forecast_usd', minWidth: 180, cellRenderer: (params: any) => <CustomSkeleton value={params.value} money attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Total Production Budget (USD)', field: 'num_total_production_budget_usd', minWidth: 180, cellRenderer: (params: any) => <CustomSkeleton value={params.value} money attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'Total Unit Cost Real (USD/unit)', field: 'num_total_production_unitcost_real', minWidth: 180, cellRenderer: (params: any) => <CustomSkeleton value={params.value} money attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Total Unit Cost Forecast (USD/unit)', field: 'num_total_production_unitcost_forecast', minWidth: 200, cellRenderer: (params: any) => <CustomSkeleton value={params.value} money attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Total Unit Cost Budget (USD/unit)', field: 'num_total_production_unitcost_budget', minWidth: 200, cellRenderer: (params: any) => <CustomSkeleton value={params.value} money attr={params.colDef.field} linha={params.data} /> },

    { headerName: 'SA', field: 'sa', minWidth: 80, cellRenderer: (params: any) => <CustomSkeleton value={params.value} attr={params.colDef.field} linha={params.data} /> },
  ];

  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    gridApi.current.sizeColumnsToFit();
  };

  const CustomSkeleton = (props: { value: any; money?: boolean; date?: boolean; porcetagem?: boolean, attr?: string, linha?: ModeloSopProduction }) => {
    return (
      <>
        {loading ? (
          <Skeleton className="width-100" />
        ) : (
          <label className='text-capitalize'>
            {(localStorage.getItem("editando") && (typeof props.value === "number" || props.date)) && (
              <label onClick={() => { atualizarValor(props.attr || "", props.linha || new ModeloSopProduction()) }} className="cursor-pointer">&nbsp;<FaEdit /></label>
            )}
            {props.value ? props.date ? (
              moment(props.value).format("MMM-YYYY")
            ) : typeof props.value === "number" ? (
              props.porcetagem ?
                formatarPorcentagem(props.value / 100, 2)
                : formatLargeNumber(props.value, props.money ? t("components.R$") : "")
            ) : (
              props.value
            ) : "-"}
          </label>
        )}
      </>
    );
  };

  async function atualizarValor(atributo: string, linha: ModeloSopProduction) {
    const valor = prompt(`Por favor, insira um valor ${atributo} (${(linha as any)[atributo]}):`);
    if (valor) {
      const _centro = new RepositorioSopProduction();
      _centro.atualizarValor(atributo, valor, linha).then((atualizadas) => {
        if (atualizadas) {
          var encontra = props.lista.find(x => x.cod_tela_sop_production === linha.cod_tela_sop_production);
          if (encontra) (encontra as any)[atributo] = isNumeric(valor) ? Number.parseFloat(valor) : valor;
          props.setLista([...props.lista]);
        }
        mostrarToast({
          titulo: "Sucesso",
          mensagem: `${atualizadas} linha(s) atualizada(s)`,
          tipo: "success",
        });
      }).catch(err => {
        mostrarToast({
          titulo: "Erro",
          mensagem: t("pages.Aconteceu algo inesperado, por favor tente novamente mais tarde!"),
          tipo: "error",
        });
      });
    }
  }

  return (
    <div className="ag-theme-alpine" style={{ height: 600, width: '100%' }}>
      <AgGridReact<ModeloSopProduction>
        rowData={props.lista}
        columnDefs={columnDefs}
        defaultColDef={{
          sortable: true,
          filter: true,
          resizable: true,
          headerClass: 'wrap-header',
          cellClass: 'numeric-cell'
        }}
        pagination={true}
        paginationPageSize={20}
        domLayout="autoHeight"
        onGridReady={onGridReady}
      />
    </div>
  );
};

export default SopProductionTable;
